import React, { useContext, useMemo, useState } from 'react';
import { Box, ToggleButton, ToggleButtonGroup, Typography } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { Line } from 'react-chartjs-2';
import takeRight from 'lodash/takeRight';

import { DataContext } from '../data/DataContext';
import { DataContextType } from '../data_types/interfaces';
import { formatHistoryDateVerbose } from '../data/formatting';
import { chronological, DIVISION_NAMES, DIVISION_THRESHOLDS } from '../data/player_insights';
import { DS, Panel, trendColor, formatSigned } from '../shared/Dashboard';
import { getTooltipDefaults } from './helpers';

const MOVING_AVG_WINDOW = 10;
const THRESHOLD_MARGIN = 30;

type Range = '30' | '100' | 'all';

const computeMovingAvg = (data: number[], window: number): (number | null)[] =>
    data.map((_, i) => {
        if (i < window - 1) return null;
        const slice = data.slice(i - window + 1, i + 1);
        return Math.round(slice.reduce((a, b) => a + b, 0) / slice.length);
    });

const LegendItem: React.FC<{ color: string; label: string; dashed?: boolean }> = ({ color, label, dashed }) => (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <Box sx={dashed
            ? { width: 16, borderTop: `2px dashed ${color}` }
            : { width: 16, height: 2, background: color }}
        />
        <Typography sx={{ fontSize: '12px', color: DS.textMuted }}>{label}</Typography>
    </Box>
);

const RatingsByRoundChart = () => {
    const context = useContext<DataContextType | undefined>(DataContext);
    const { historyData, summaryData } = context || {};
    const [range, setRange] = useState<Range>('100');

    const history = useMemo(() => chronological(historyData ?? []), [historyData]);

    const series = useMemo(() => {
        // Moving average is computed on the full season so the first visible
        // points of a shorter range still have a value.
        const movingAvg = computeMovingAvg(history.map(d => d.currentRating), MOVING_AVG_WINDOW);
        const count = range === 'all' ? history.length : Number(range);
        const visible = takeRight(history, count);
        return {
            rounds: visible,
            ratings: visible.map(d => d.currentRating),
            labels: visible.map(d => d.matchNum),
            movingAvg: takeRight(movingAvg, count),
        };
    }, [history, range]);

    const highest: number = summaryData?.highest_rating ?? 0;
    const rangeChange = series.ratings.length > 1 ? series.ratings[series.ratings.length - 1] - series.ratings[0] : 0;
    const dense = series.ratings.length > 120;

    // Division thresholds near the visible ratings (Rookie's 0 never is).
    // The margin lets a line you're closing in on show before you reach it.
    const thresholds = useMemo(() => {
        if (!series.ratings.length) return [];
        const min = Math.min(...series.ratings) - THRESHOLD_MARGIN;
        const max = Math.max(...series.ratings) + THRESHOLD_MARGIN;
        return DIVISION_THRESHOLDS
            .map((value, division) => ({ value, name: DIVISION_NAMES[division] }))
            .filter(t => t.value > 0 && t.value >= min && t.value <= max);
    }, [series.ratings]);

    const annotations: Record<string, any> = {};
    thresholds.forEach(t => {
        annotations[`division${t.value}`] = {
            type: 'line',
            yMin: t.value,
            yMax: t.value,
            adjustScaleRange: true,
            borderColor: alpha(DS.textMuted, 0.4),
            borderWidth: 1,
            borderDash: [2, 4],
            // Left-aligned so it never collides with the peak label on the right
            label: {
                display: true,
                content: `${t.name} ${t.value}`,
                position: 'start',
                color: DS.textMuted,
                font: { size: 10, weight: 'bold' },
                backgroundColor: DS.surface,
                padding: { x: 6, y: 2 },
                borderRadius: 4,
            },
        };
    });
    if (highest > 0) {
        annotations.highestRating = {
            type: 'line',
            yMin: highest,
            yMax: highest,
            // Only stretch the axis to the peak on the full season view
            adjustScaleRange: range === 'all',
            borderColor: alpha(DS.gold, 0.6),
            borderWidth: 1.5,
            borderDash: [6, 4],
            label: {
                display: true,
                content: `Peak ${highest}`,
                position: 'end',
                color: DS.gold,
                font: { size: 11, weight: 'bold' },
                backgroundColor: alpha(DS.gold, 0.12),
                padding: { x: 8, y: 4 },
                borderRadius: 6,
            },
        };
    }

    return (
        <Panel
            title="Rating progression"
            subtitle={
                <>
                    {series.rounds.length} rounds ·{' '}
                    <span style={{ color: trendColor(rangeChange), fontWeight: 700 }}>{formatSigned(rangeChange)}</span> over this range
                </>
            }
            delay={0.05}
            action={
                <ToggleButtonGroup
                    size="small"
                    exclusive
                    value={range}
                    onChange={(_, value) => value && setRange(value)}
                    sx={{
                        '& .MuiToggleButton-root': {
                            px: 1.5,
                            py: 0.4,
                            textTransform: 'none',
                            fontSize: '12px',
                            fontWeight: 600,
                            color: DS.textMuted,
                            borderColor: DS.border,
                        },
                        '& .Mui-selected': {
                            color: `${DS.text} !important`,
                            background: `${alpha(DS.brand, 0.18)} !important`,
                        },
                    }}
                >
                    <ToggleButton value="30">Last 30</ToggleButton>
                    <ToggleButton value="100">Last 100</ToggleButton>
                    <ToggleButton value="all">Season</ToggleButton>
                </ToggleButtonGroup>
            }
        >
            <Box sx={{ display: 'flex', gap: 2.5, flexWrap: 'wrap', mb: 1.5 }}>
                <LegendItem color={DS.brand} label="Rating" />
                <LegendItem color={DS.info} label={`${MOVING_AVG_WINDOW}-round average`} dashed />
                <LegendItem color={DS.gold} label="Season peak" dashed />
                {thresholds.length > 0 && <LegendItem color={DS.textMuted} label="Division threshold" dashed />}
            </Box>

            <Box sx={{ height: { xs: 240, md: 320 } }}>
                <Line
                    data={{
                        labels: series.labels,
                        datasets: [
                            {
                                data: series.ratings,
                                label: 'Rating',
                                borderColor: DS.brand,
                                borderWidth: 2,
                                backgroundColor: (ctx) => {
                                    const { ctx: canvas, chartArea } = ctx.chart;
                                    if (!chartArea) return 'transparent';
                                    const gradient = canvas.createLinearGradient(0, chartArea.top, 0, chartArea.bottom);
                                    gradient.addColorStop(0, alpha(DS.brand, 0.25));
                                    gradient.addColorStop(1, alpha(DS.brand, 0));
                                    return gradient;
                                },
                                fill: true,
                                tension: 0.3,
                                pointBackgroundColor: series.rounds.map(d => trendColor(d.ratingChange)),
                                pointBorderColor: series.rounds.map(d => trendColor(d.ratingChange)),
                                pointRadius: dense ? 0 : 2.5,
                                pointHoverRadius: 5,
                                pointHoverBorderWidth: 2,
                                pointHoverBorderColor: '#fff',
                                order: 2,
                            },
                            {
                                data: series.movingAvg,
                                label: `${MOVING_AVG_WINDOW}-round avg`,
                                borderColor: alpha(DS.info, 0.8),
                                borderWidth: 1.5,
                                borderDash: [5, 4],
                                fill: false,
                                tension: 0.4,
                                pointRadius: 0,
                                pointHoverRadius: 0,
                                spanGaps: true,
                                order: 1,
                            },
                        ],
                    }}
                    options={{
                        responsive: true,
                        maintainAspectRatio: false,
                        animation: { duration: 500 },
                        interaction: { mode: 'index', intersect: false },
                        plugins: {
                            legend: { display: false },
                            tooltip: {
                                ...getTooltipDefaults({ palette: { text: { primary: DS.text, secondary: DS.textMuted }, background: { default: '#161c24' } } }),
                                padding: 12,
                                borderColor: DS.borderStrong,
                                borderWidth: 1,
                                callbacks: {
                                    title: (items) => `Round #${items[0].label}`,
                                    label: (item) => {
                                        if (item.datasetIndex === 1) {
                                            return item.raw !== null ? ` ${MOVING_AVG_WINDOW}-avg: ${item.raw}` : '';
                                        }
                                        const round = series.rounds[item.dataIndex];
                                        return [
                                            ` Rating: ${item.raw} (${formatSigned(round.ratingChange)})`,
                                            ` Finish: ${round.position}`,
                                            ` ${formatHistoryDateVerbose(round.date)}`,
                                        ];
                                    },
                                    labelColor: (item) => {
                                        const color = item.datasetIndex === 1
                                            ? DS.info
                                            : trendColor(series.rounds[item.dataIndex].ratingChange);
                                        return { borderColor: color, backgroundColor: color };
                                    },
                                },
                            },
                            annotation: { annotations },
                        },
                        scales: {
                            y: {
                                border: { display: false },
                                grid: { color: 'rgba(145,158,171,0.08)' },
                                ticks: { color: DS.textMuted, font: { size: 11 }, padding: 4, maxTicksLimit: 6 },
                            },
                            x: {
                                type: 'linear',
                                // Pin to the visible rounds; otherwise the axis rounds out to
                                // the next "nice" tick and leaves empty space on the left.
                                // Half a round either side keeps the end dots inside the chart.
                                min: series.labels[0] - 0.5,
                                max: series.labels[series.labels.length - 1] + 0.5,
                                border: { display: false },
                                grid: { display: false },
                                ticks: {
                                    color: DS.textMuted,
                                    font: { size: 11 },
                                    maxTicksLimit: 10,
                                    precision: 0,
                                    // Don't label the half-round padding at either end
                                    includeBounds: false,
                                    callback: (val) => `#${val}`,
                                },
                            },
                        },
                    }}
                />
            </Box>
        </Panel>
    );
};

export default RatingsByRoundChart;
